import { z } from "zod";

export const TemplateTierInputSchema = z.object({
  minPeople: z.number().int().positive(),
  maxPeople: z.number().int().positive(),
  pricePerHour: z.number().int().positive(),
  pricePerHourOffSiteNear: z.number().int().positive().nullable().optional(),
  pricePerHourOffSiteFar: z.number().int().positive().nullable().optional(),
  extraPersonPrice: z.number().int().positive().nullable().optional(),
  sortOrder: z.number().int().optional(),
});

export type TemplateTierInput = z.infer<typeof TemplateTierInputSchema>;

export function computeTemplateSizeRangeFromTiers(tiers: TemplateTierInput[]): {
  groupSizeMin: number;
  groupSizeMax: number;
} {
  const sorted = [...tiers].sort((a, b) => a.minPeople - b.minPeople);
  const first = sorted[0]!;
  const last = sorted[sorted.length - 1]!;
  return {
    groupSizeMin: first.minPeople,
    groupSizeMax: last.maxPeople,
  };
}

export function refinePrivateSharedTemplateTiers(
  tiers: TemplateTierInput[],
  ctx: z.RefinementCtx,
  pathPrefix: (string | number)[] = ["tiers"],
): void {
  if (tiers.length === 0) {
    ctx.addIssue({
      code: "custom",
      message: "ຕ້ອງມີ tier ລາຄາຢ່າງໜ້ອຍ 1 ແຖວ",
      path: pathPrefix,
    });
    return;
  }

  const sorted = [...tiers]
    .map((t, index) => ({ ...t, index }))
    .sort((a, b) => a.minPeople - b.minPeople);

  for (const tier of sorted) {
    if (tier.minPeople > tier.maxPeople) {
      ctx.addIssue({
        code: "custom",
        message: "ຈຳນວນຄົນຂັ້ນຕ່ຳຕ້ອງບໍ່ເກີນສູງສຸດ",
        path: [...pathPrefix, tier.index, "minPeople"],
      });
    }
    if (tier.pricePerHour <= 0) {
      ctx.addIssue({
        code: "custom",
        message: "ລາຄາຕໍ່ຊົ່ວໂມງຕ້ອງເປັນຈຳນວນບວກ",
        path: [...pathPrefix, tier.index, "pricePerHour"],
      });
    }
  }

  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i]!;
    const next = sorted[i + 1];
    if (next && current.maxPeople >= next.minPeople) {
      ctx.addIssue({
        code: "custom",
        message: "ຊ່ວງຈຳນວນຄົນ tier ຊ້ຳກັນ",
        path: [...pathPrefix, next.index, "minPeople"],
      });
    }
    if (next && current.maxPeople + 1 !== next.minPeople) {
      ctx.addIssue({
        code: "custom",
        message: "tier ຕ້ອງຕໍ່ເນື່ອງກັນ (ບໍ່ມີຊ່ອງວ່າງ)",
        path: [...pathPrefix, next.index, "minPeople"],
      });
    }
  }

  const top = sorted[sorted.length - 1]!;
  for (let i = 0; i < sorted.length - 1; i++) {
    const tier = sorted[i]!;
    if (tier.extraPersonPrice != null && tier.extraPersonPrice > 0) {
      ctx.addIssue({
        code: "custom",
        message: "ຄ່າຄົນເພີ່ມໃຊ້ໄດ້ tier ສູງສຸດເທົ່ານັ້ນ",
        path: [...pathPrefix, tier.index, "extraPersonPrice"],
      });
    }
  }

  if (
    top.extraPersonPrice != null &&
    top.extraPersonPrice <= 0
  ) {
    ctx.addIssue({
      code: "custom",
      message: "ຄ່າຄົນເພີ່ມຕ້ອງເປັນຈຳນວນບວກ",
      path: [...pathPrefix, top.index, "extraPersonPrice"],
    });
  }
}
